import { useState } from 'react';
import { Loader2, Check } from 'lucide-react';
import { createInterest } from '@/lib/exchangeApi';

const inputCls =
  'w-full rounded-xl border border-black/10 bg-white px-4 py-3 text-sm text-ink placeholder:text-ink-muted focus:outline-none focus:border-brand-teal focus:ring-1 focus:ring-brand-teal/40 transition-colors';

const Field = ({ label, required, children }) => (
  <div>
    <label className="block text-sm font-semibold text-ink mb-1.5">
      {label} {required && <span className="text-brand-orange">*</span>}
    </label>
    {children}
  </div>
);

const InterestPage = () => {
  const [form, setForm] = useState({ organization: '', name: '', role: '', email: '', city: '', help_with: '' });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const emailValid = /\S+@\S+\.\S+/.test(form.email);
  const valid = form.organization.trim() && form.name.trim() && emailValid;

  const submit = async () => {
    if (!valid || submitting) return;
    setSubmitting(true);
    setError('');
    try {
      await createInterest({
        organization: form.organization.trim(),
        name: form.name.trim(),
        role: form.role.trim(),
        email: form.email.trim(),
        city: form.city.trim(),
        help_with: form.help_with.trim(),
      });
      setDone(true);
    } catch {
      setError('Something went wrong — please try again.');
      setSubmitting(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto px-6 py-12" data-testid="interest-page">
      <header className="mb-10">
        <span className="inline-block text-xs font-bold uppercase tracking-[0.16em] text-brand-orange mb-3">Get in touch</span>
        <h1 className="text-4xl md:text-5xl font-bold text-ink leading-tight mb-4">
          Bring the AI Lab to your organization
        </h1>
        <p className="text-lg text-ink-secondary leading-relaxed">
          Register interest below. This isn't a booking — it's a way to hear first when the next AI Lab
          takes shape.
        </p>
      </header>

      {done ? (
        <div className="bg-white rounded-3xl border border-brand-teal/20 shadow-sm p-10 text-center" data-testid="interest-confirmation">
          <div className="w-14 h-14 rounded-2xl bg-brand-teal/10 text-brand-teal flex items-center justify-center mx-auto mb-5">
            <Check size={28} strokeWidth={2.5} />
          </div>
          <p className="text-xl font-bold text-ink mb-2">Thank you — you're on the list.</p>
          <p className="text-ink-secondary">We'll write when the next AI Lab takes shape.</p>
        </div>
      ) : (
        <div className="bg-white rounded-3xl border border-black/[0.07] shadow-sm p-6 md:p-8">
          <div className="space-y-5">
            <Field label="Organization" required>
              <input className={inputCls} value={form.organization} onChange={set('organization')}
                placeholder="Your organization's name" data-testid="interest-organization" />
            </Field>
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Your name" required>
                <input className={inputCls} value={form.name} onChange={set('name')}
                  placeholder="Full name" data-testid="interest-name" />
              </Field>
              <Field label="Role">
                <input className={inputCls} value={form.role} onChange={set('role')}
                  placeholder="e.g. Program Director" data-testid="interest-role" />
              </Field>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Email" required>
                <input type="email" className={inputCls} value={form.email} onChange={set('email')}
                  placeholder="you@organization.org" data-testid="interest-email" />
              </Field>
              <Field label="City">
                <input className={inputCls} value={form.city} onChange={set('city')}
                  placeholder="e.g. Bengaluru" data-testid="interest-city" />
              </Field>
            </div>
            <Field label="What would your team want help with?">
              <textarea className={`${inputCls} resize-y`} rows={4} value={form.help_with} onChange={set('help_with')}
                placeholder="Tell us a little about what your team is hoping to build or learn." data-testid="interest-help-with" />
            </Field>

            {error && <p className="text-sm text-red-600" data-testid="interest-error">{error}</p>}

            <button
              onClick={submit}
              disabled={!valid || submitting}
              data-testid="interest-submit-btn"
              className={`w-full inline-flex items-center justify-center gap-2 py-3.5 rounded-full font-bold text-sm transition-colors ${
                valid && !submitting
                  ? 'bg-brand-orange text-white hover:bg-brand-orange-hover'
                  : 'bg-black/10 text-ink-muted cursor-not-allowed'
              }`}
            >
              {submitting && <Loader2 size={15} className="animate-spin" />}
              {submitting ? 'Submitting…' : 'Register interest'}
            </button>

            <p className="text-xs text-ink-muted leading-relaxed">
              This form stores only what you enter above, on this platform's infrastructure, and is used
              solely to contact you about future AI Lab sessions.
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default InterestPage;
